import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import ScrollArea from 'react-scrollbar';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import ReactPanZoom from "@ajainarayanan/react-pan-zoom";

import { PageTitle } from '../PageTitle';
import { ContentService, DevicesService, EntityService, GroupingsService } from '../../services';
import { ScrollModule } from '../ScrollModule';
import { EntityIcon } from '../Entity';
import i18next from "i18next";

const DeviceViewGroupMetadata = ({ match }) => {
  const [device, setDevice] = useState(null);
  const [amtGrouping, setAMTGrouping] = useState(null);
  const [errorMessage, setErrorMessage] = useState(null);

  const deviceId = match.params.deviceId;
  const amtId = match.params.amtId;

  const findGroupingForAMT = (grouping, validGroupsForAMT) => {
    if ((grouping.deviceGroup && validGroupsForAMT.includes(grouping.deviceGroup.id)) ||
        (grouping.deviceGroupId && validGroupsForAMT.includes(grouping.deviceGroupId))) {
      return grouping;
    } else if(grouping.parent != null) {
      return findGroupingForAMT(grouping.parent, validGroupsForAMT);
    } else {
      return null;
    }
  };

  useEffect(() => {
    DevicesService.get(deviceId).then((response) => {
      setDevice(response.device);

      let validGroupsForAMT = [];
      response.device.deviceType.metadataTypes.forEach(metadataType => {
        metadataType.groupAssociableMetadataTypes.forEach(amt => {
          // TODO: why can === not be used?
          if (amt.id == amtId) {
            validGroupsForAMT = amt.validForGroups;
          }
        })
      });

      // find parent grouping of this device that is associated with the incoming AMT id
      let amtGrouping = findGroupingForAMT(response.device.deviceGrouping, validGroupsForAMT);

      if (amtGrouping == null) {
        setErrorMessage(i18next.t("messages.could-not-find-support"));
      } else {
        GroupingsService.get(amtGrouping.id, true).then((response) => {
          setAMTGrouping(response.grouping);
        }).catch((error) => {
          console.error(error);
          setErrorMessage(i18next.t("messages.could-not-load-grouping"));
        });
      }
    }).catch((error) => {      
      console.error(error);
      setErrorMessage(i18next.t("messages.could-not-load-device-updating"));
    });
  }, [deviceId, amtId]);

  if (errorMessage) {
    return (
      <div>
        <div className="row">
          <PageTitle title="Error" backLink={`/config/devices/view/${deviceId}`} />
          <p className="op-content-box">{errorMessage}</p>
        </div>
      </div>
    );
  } if (device && amtGrouping) {
    return (
        <div>
          <div className="row">
            <PageTitle title={`${amtGrouping.name} ${i18next.t('pages.view-group-metadata.floor-map')}`} backLink={`/config/devices/view/${deviceId}`} />
          </div>
          <EntityViewGroupMetadataInterface entity={device} grouping={amtGrouping} />
        </div>
    );
  } else {
    return <div className="loader">{ i18next.t('loading') }</div>;
  }
}

const EntityViewGroupMetadataInterface = ({ grouping, entity }) => {
    const [loading, setLoading] = useState(true);
    const [dx, setDX] = useState(0);
    const [dy, setDY] = useState(0);
    const [zoom, setZoom] = useState(1);
    const [mapURL, setMapURL] = useState(null);
    const [currentEntities, setCurrentEntities] = useState([]);

    const zoomIn = () => {
        setZoom(zoom + 0.2);
    };

    const zoomOut = () => {
        if (zoom > 0.4) {
            setZoom(zoom - 0.2);
        }
    };

    const onPan = (dx, dy) => {
        setDX(dx);
        setDY(dy);
    };

    useEffect(() => {
        if (grouping.amtGroupingSupportEntries) {
            setLoading(true);

            const floorPlanAMTData = grouping.amtGroupingSupportEntries.find((supportEntry) => supportEntry.groupAssociableMetadataType.token === "floor_plan_location");

            if (floorPlanAMTData) {
              let requests = [];

              requests.push(ContentService.getDeviceGroupingImage(grouping.id, floorPlanAMTData.value).then((response) => {
                  setMapURL(response);
              }));

              requests.push(EntityService.findAllForGrouping(grouping.id).then((response) => {
                setCurrentEntities(response.entities);
              }));

              Promise.all(requests).then((_) => {
                  setLoading(false);
              }).catch((error) => {
                  console.error(error);
                  setMapURL(null);
                  setLoading(false);
              });
            } else {
                setMapURL(null);
                setLoading(false);
            }
        } else {
            setMapURL(null);
            setLoading(false);
        }
  }, [grouping]);

  if (loading) {
      return <div className="loader">{i18next.t("loading")}.</div>;
  } else if (!mapURL || mapURL === "images/icon/no_image_icon.svg") {
    return (
      <div>
          <p className="op-info">
              {i18next.t("messages.no-floor-plan-image", { grouping: grouping.name })} <Link to={`/admin/groupings/edit/${grouping.id}`}>{i18next.t("messages.no-floor-plan-image-2")}</Link>.
          </p>
      </div>
    );
  } else {
    return (
        <div className="row">
            <div className="op-content-box">
                <p className="op-info"> {i18next.t("pages.view-group-metadata.you-are-viewing")} <strong>{entity.displayName}</strong>. </p>

                <div className="container-fluid">
                    <div className="col-sm-12">
                        <ScrollModule />
                        <ScrollArea speed={0.8} className="scroll-container" contentClassName="scrollable-content" horizontal={true}>
                            <ReactPanZoom zoom={zoom} pandx={dx} pandy={dy} onPan={onPan}>
                                <div className="store-map-image-wrapper" style={{ position: "absolute", backgroundImage: `url(${mapURL})`, width: 800, height: 600, backgroundSize: "800px 600px", opacity: 0.4, left: "50%", marginLeft: -400 }}></div>
                                <div className="store-map-image-wrapper" style={{ position: "relative", backgroundImage: `url(${mapURL})`, width: 800, height: 600, backgroundSize: "800px 600px", visibility: "hidden" }}>
                                    { 
                                      grouping.amtDevices['floor_plan_location'].map((device) => {
                                        const currentEntity = currentEntities.find((ce) => ce.serialNumber === device.serialNumber);
                                        if (currentEntity && currentEntity.floorPlanX && currentEntity.floorPlanY) {
                                          const popoverHoverFocus = <Tooltip id="popover-trigger-hover-focus">{currentEntity.displayName}</Tooltip>;
                                          var style = {
                                                        opacity: 0.3,
                                                        position: "absolute",
                                                        top: currentEntity.floorPlanY,
                                                        left: currentEntity.floorPlanX,
                                                        width: `${24 / zoom}px`,
                                                        height: `${24 / zoom}px`,
                                                        visibility: "visible",
                                                    };

                                          if (entity.serialNumber === currentEntity.serialNumber) {
                                            style["opacity"] = 1.0;
                                            style["boxShadow"] = "0 4px 14px rgba(0,0,0,.78)";
                                            style["borderRadius"] = "50%";
                                          }
                                          
                                          return (
                                            <OverlayTrigger key={currentEntity.serialNumber} trigger={['hover', 'focus']} placement={currentEntity.floorPlanX > 300 ? 'left' : 'right'} overlay={popoverHoverFocus}>
                                              <EntityIcon style={style} entity={currentEntity} />
                                            </OverlayTrigger>
                                          );
                                        } else {
                                          return null;
                                        }
                                      }).filter((overlay) => overlay != null)
                                    }
                                </div>
                            </ReactPanZoom>
                        </ScrollArea>

                        <div className="op-floor-plan-zoom">
                            <div className="row">
                                <div className="col-sm-12">
                                    <button onClick={zoomIn} type="button" className="btn btn-default btn-block btn-following-row">
                                        <span>+</span>
                                    </button>
                                </div>
                            </div>
                            <div className="row">
                                <div className="col-sm-12">
                                    <button onClick={zoomOut} type="button" className="btn btn-default btn-block btn-following-row">
                                        <span style={{ fontSize: "18px", fontWeight: "600", lineHeight: "22px" }}>-</span>
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
  }
}

export default DeviceViewGroupMetadata;
